import { stringLiteral } from '@execonline-inc/decoders';
import { Decoder, array, field, number, string, succeed } from 'jsonous';
import { Result, err, ok } from 'resulty';
import { resourceDecoder } from '../../../../../Resource/Decoders';
import {
  FailedParticipant,
  ImportedParticipants,
  ImportedParticipantsResource,
  ParticipantImportFileData,
  ParticipantImportFileRow,
  SuccessfulParticipant,
} from '../Types';

const failedParticipantDecoder: Decoder<FailedParticipant> = succeed({})
  .assign('kind', field('kind', stringLiteral('failed')))
  .assign('email', field('email', string))
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string))
  .assign('errorMessage', field('error_message', string));

const successfulParticipantDecoder: Decoder<SuccessfulParticipant> = succeed({})
  .assign('kind', field('kind', stringLiteral('successful')))
  .assign('id', field('id', number))
  .assign('email', field('email', string))
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string));

const importedParticipantsDecoder: Decoder<ImportedParticipants> = succeed({})
  .assign('failedParticipants', field('failed_participants', array(failedParticipantDecoder)))
  .assign(
    'successfulParticipants',
    field('successful_participants', array(successfulParticipantDecoder)),
  )
  .assign('formattingIssue', field('formatting_issue', array(failedParticipantDecoder)))
  .assign(
    'participantsOfDifferentOrg',
    field('participants_of_different_org', array(failedParticipantDecoder)),
  )
  .assign(
    'participantsOfSameOrg',
    field('participants_of_same_org', array(failedParticipantDecoder)),
  );

export const importedParticipantsResourceDecoder: Decoder<ImportedParticipantsResource> =
  resourceDecoder(importedParticipantsDecoder);

export const csvDataDecoder = <T>(decoder: Decoder<T>): Decoder<T[]> =>
  new Decoder((value: T) => {
    if (!(value instanceof Array)) {
      return err(`'${value}' is not an array`);
    }

    return value.reduce((memo: Result<string, T[]>, element) => {
      const result = decoder.decodeAny(element);
      return memo.andThen((results) => result.map((v) => results.concat([v])));
    }, ok([]));
  });

const rowsDecoder: Decoder<ParticipantImportFileRow> = succeed({})
  .assign('firstname', field('firstname', string))
  .assign('lastname', field('lastname', string))
  .assign('email', field('email', string));

export const fileDecoder = (file: File): Decoder<ParticipantImportFileData> =>
  csvDataDecoder(rowsDecoder).andThen((rows) => {
    return succeed({
      fileName: file.name,
      rows,
    });
  });
